import { batch } from '@preact/signals-react';
import { posthog } from 'posthog-js';

import { authError, isAuthenticated, isLoading } from '../state/auth';
import { clearOrganizationState } from '../state/organization';
import {
    email,
    firstName,
    id,
    lastName,
    profilePictureUrl,
    role,
    workosId,
} from '../state/user';
import { AuthenticatedUser } from '../Types/User';
import { handleOrganizationSelection } from './organizationService';
import { syncPillarData } from './pillarSyncService';

interface AuthResponse {
    authorizationUrl: string;
}

const updateUserState = (userData: AuthenticatedUser) => {
    batch(() => {
        firstName.value = userData.firstName || '';
        lastName.value = userData.lastName || '';
        workosId.value = userData.workosId || '';
        id.value = userData.id || -1;
        email.value = userData.email || '';
        profilePictureUrl.value = userData.profilePictureUrl || '';
        role.value = userData.role || '';
    });
};

const clearUserState = (): void => {
    batch(() => {
        firstName.value = '';
        lastName.value = '';
        workosId.value = '';
        id.value = -1;
        email.value = '';
        profilePictureUrl.value = '';
    });
};

export const fetchUserData = async (retries = 2): Promise<void> => {
    try {
        // attempt to get a user profile from a session ID, this checks if they have a valid login with workOS
        const response = await fetch('/user', {
            method: 'GET',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Cache-Control': 'no-cache',
            },
        });

        if (!response.ok) {
            // If we get a 500 error, it might be temporary
            if (response.status === 500 && retries > 0) {
                await new Promise((resolve) => setTimeout(resolve, 2000));
                return fetchUserData(retries - 1);
            }
            throw new Error('Failed to fetch user data');
        }
        const userData: AuthenticatedUser = await response.json();
        const alabEmails = [
            'assurancelab.cpa',
            'assurancelab.com.au',
            'pillargrc.com',
            'sensiba.com',
        ];
        if (
            userData.redirectUrl &&
            (userData.role == 'member' ||
                !alabEmails.includes(userData.email.split('@')[1]))
        ) {
            //window.location.href = userData.redirectUrl;
            //return;
        }
        updateUserState(userData);
        isAuthenticated.value = true;

        posthog.identify(userData.email, {
            email: userData.email,
            name: `${userData.firstName} ${userData.lastName}`,
            id: userData.workosId,
            pillarId: userData.id,
        });

        // Sync Pillar data first (non-blocking)
        // This may set organization from WorkOS login selection
        syncPillarData()
            .then((pillarData) => {
                if (pillarData?.success && pillarData.role) {
                    // Update the role signal with the synced role from Pillar
                    role.value = pillarData.role;
                    // Update pillarId if available
                    if (pillarData.pillarId) {
                        id.value = parseInt(pillarData.pillarId) || id.value;
                    }
                }

                // If organization was NOT set from WorkOS login, handle manual selection
                if (!pillarData?.organization) {
                    handleOrganizationSelection(userData.lastUsedOrgId).catch((error) => {
                        console.warn('[AUTH] Organization selection failed:', error);
                    });
                }
            })
            .catch((error) => {
                console.warn(
                    '[AUTH] Pillar sync failed, falling back to manual org selection:',
                    error,
                );
                // Fall back to manual organization selection
                handleOrganizationSelection(userData.lastUsedOrgId).catch((orgError) => {
                    console.warn('[AUTH] Organization selection also failed:', orgError);
                });
            });
    } catch (error) {
        // Clear user data in signals
        clearUserState();
        isAuthenticated.value = false;

        if (error instanceof Error) {
            console.error('Error fetching user data:', error);
            authError.value = error?.message;
        } else {
            console.error('An unexpected error occurred:', error);
            authError.value = 'An unexpected error occurred during authentication.';
        }

        posthog.reset();

        // Only redirect to login if we've exhausted retries
        if (
            error instanceof Error &&
            error.message.includes('Failed to fetch user data')
        ) {
            // Add a small delay before redirecting to login
            setTimeout(() => {
                startAuthFlow();
            }, 1000);
        } else {
            // For other errors, try to start auth flow immediately
            startAuthFlow();
        }
    }
};

const startAuthFlow = async (): Promise<void> => {
    isLoading.value = true;
    authError.value = null;
    try {
        const response = await fetch('/auth', {
            method: 'GET',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
            },
        });
        if (!response.ok) {
            throw new Error('Failed to fetch authorization URL');
        }
        const data: AuthResponse = await response.json();
        isAuthenticated.value = false; // Set to false since we're redirecting to login
        isLoading.value = false;
        window.location.href = data.authorizationUrl;
    } catch (error: unknown) {
        isAuthenticated.value = false;
        isLoading.value = false;
        if (error instanceof Error) {
            console.error(error.message);
            authError.value = error.message;
        } else {
            // Handle cases where the error might not be an Error object
            console.error('An unexpected error occurred:', error);
            authError.value = 'An unexpected error occurred';
        }
    }
};

export const signOutUser = async (): Promise<void> => {
    // Import sessionManager dynamically to avoid circular dependency
    const { sessionManager } = await import('./sessionManager');
    sessionManager.stop();

    await fetch('/signout');
    document.cookie.split(';').forEach((cookie) => {
        const name = cookie.split('=')[0].trim();
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    clearUserState();
    clearOrganizationState();
    posthog.reset();
    window.location.reload();
};
